/**
 * Component: EmployeeShiftCard
 * @description Displays a card representing an employee's assigned shift or an unassigned shift, including role, hours worked, and overtime.
 *
 * @props
 *   @param {string} role - The role for the shift (default is "Unassigned").
 *   @param {Object|null} assignedEmployee - The assigned employee's details, including `name` and `profileImageUrl`.
 *   @param {string} totalHours - The total hours worked by the employee (default is "0 hrs").
 *   @param {string} overtimeHours - The overtime hours worked by the employee (default is "0 hrs").
 *
 * @render
 *   - Displays an avatar for the employee or a placeholder icon if unassigned.
 *   - Shows the employee's name (or role if unassigned), total hours, and overtime if applicable.
 *
 * @styles
 *   - Styled components are used for the card, avatar, and text to match the application's theme.
 *
 * @example
 * <EmployeeShiftCard
 *   role="Bartender"
 *   assignedEmployee={{ name: "John Doe", profileImageUrl: "/path/to/image.jpg" }}
 *   totalHours="8 hrs"
 *   overtimeHours="2 hrs"
 * />
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Person as PersonIcon } from '@mui/icons-material';
import { StyledCard, StyledCardContent, OvertimeText, WhiteText } from './EmployeeShiftCard.styles';
import { StyledAvatar } from '../StyledAvatar/StyledAvatar';

const EmployeeShiftCard = ({
  role = 'Unassigned',
  assignedEmployee = null,
  totalHours = '0 hrs',
  overtimeHours = '0 hrs',
}) => {
  // Determine if the card is for an assigned or unassigned shift
  const isAssigned = !!assignedEmployee;

  return (
    <StyledCard elevation={0}>
      <StyledAvatar
        size='small'
        src={isAssigned ? assignedEmployee.profileImageUrl : ''}
        alt={isAssigned ? assignedEmployee.name : role}
      >
        {!isAssigned && <PersonIcon />}
      </StyledAvatar>
      <StyledCardContent>
        <WhiteText variant="h6">{isAssigned ? assignedEmployee.name : role}</WhiteText>
        {totalHours && <OvertimeText variant="body2">{totalHours}</OvertimeText>}
        {overtimeHours && parseFloat(overtimeHours) > 0 && (
          <OvertimeText variant="body2">Overtime: {overtimeHours}</OvertimeText>
        )}
      </StyledCardContent>
    </StyledCard>
  );
};

EmployeeShiftCard.propTypes = {
  role: PropTypes.string,
  assignedEmployee: PropTypes.shape({
    name: PropTypes.string,
    profileImageUrl: PropTypes.string,
  }),
  totalHours: PropTypes.string,
  overtimeHours: PropTypes.string,
};

export default EmployeeShiftCard;
